import { useCallback, useEffect, useRef, useState } from 'react';

import type { Strategy } from '@/features/auth/login/types/login';
import { processOAuthStatus } from '@/features/auth/login/utils/oauthHandlers';
import { initiateOAuthRedirect } from '@/features/auth/login/utils/oauthRedirect';
import { parseClerkOAuthStatus } from '@/features/auth/login/utils/parseClerkOAuthStatus';
import { CLERK_ERROR_CODE, CLERK_OAUTH_STATUS } from '@/features/auth/shared/consts/clerk';
import type { AuthError } from '@/features/auth/shared/types/auth';
import { mapClerkError } from '@/features/auth/shared/utils/clerkErrorMapper';
import { extractClerkErrorMessage } from '@/features/auth/shared/utils/extractClerkError';
import { useSignIn, useSignUp } from '@clerk/clerk-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

interface UseOAuthLoginReturn {
  handleSocialLogin: (strategy: Strategy) => Promise<void>;
  isOAuthReady: boolean;
  isCreatingAccount: boolean;
  oauthLoading: boolean;
}

export const useOAuthLogin = (
  setError: (error: AuthError | null) => void,
  setLoading: (loading: boolean) => void
): UseOAuthLoginReturn => {
  const { isLoaded: isSignInLoaded, signIn, setActive } = useSignIn();
  const { isLoaded: isSignUpLoaded, signUp } = useSignUp();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const oauthProcessedRef = useRef(false);

  const hasOAuthParams =
    typeof window !== 'undefined' && window.location.search.includes('__clerk_') && !oauthProcessedRef.current;

  const getInitialCreatingAccountState = (): boolean => {
    if (!hasOAuthParams || !signIn || !signUp) return false;
    const searchParams = new URLSearchParams(window.location.search);
    const status = parseClerkOAuthStatus(signIn, signUp, searchParams);
    return status.status === CLERK_OAUTH_STATUS.NEEDS_ACCOUNT_CREATION;
  };

  const [isCreatingAccount, setIsCreatingAccount] = useState(getInitialCreatingAccountState);
  const [oauthLoading, setOauthLoading] = useState(hasOAuthParams);
  const [isOAuthReady, setIsOAuthReady] = useState(false);

  const handleClerkError = useCallback(
    (error: unknown, setErrorFn: (error: AuthError | null) => void) => {
      const mappedError = mapClerkError(error, t);
      setErrorFn(mappedError);
      toast.error(mappedError.message);
    },
    [t]
  );

  const handleSignUpNotLoaded = useCallback(() => {
    setError({ type: 'email', message: t('error.login.accountNotFound') });
    toast.error(t('error.login.accountNotFound'));
    setLoading(false);
  }, [t, setError, setLoading]);

  const handleSignUpRedirect = useCallback(
    async (strategy: Strategy) => {
      try {
        await initiateOAuthRedirect(signUp, strategy);
      } catch (signUpErr) {
        handleClerkError(signUpErr, setError);
        setLoading(false);
      }
    },
    [signUp, handleClerkError, setError, setLoading]
  );

  const handleOAuthResult = useCallback(async () => {
    const searchParams = new URLSearchParams(window.location.search);
    const status = parseClerkOAuthStatus(signIn, signUp, searchParams);
    setOauthLoading(false);

    if (status.status === 'idle') {
      setIsOAuthReady(true);
      return;
    }

    oauthProcessedRef.current = true;
    const handlerContext = { setActive, navigate, t, setError, setOauthLoading };
    const accountCreationContext = { ...handlerContext, signUp, setIsCreatingAccount };

    await processOAuthStatus(status, handlerContext, accountCreationContext);
    setIsOAuthReady(true);
  }, [signIn, signUp, setActive, navigate, t, setError, setIsCreatingAccount]);

  useEffect(() => {
    if (!isSignInLoaded || !isSignUpLoaded || oauthProcessedRef.current) {
      return;
    }

    void handleOAuthResult();
  }, [isSignInLoaded, isSignUpLoaded, handleOAuthResult]);

  const handleSocialLogin = useCallback(
    async (strategy: Strategy) => {
      if (!isSignInLoaded) {
        return;
      }

      setError(null);
      setLoading(true);

      try {
        await initiateOAuthRedirect(signIn, strategy);
      } catch (err) {
        const clerkErrorMessage = extractClerkErrorMessage(err);

        if (clerkErrorMessage?.includes(CLERK_ERROR_CODE.EXTERNAL_ACCOUNT_NOT_FOUND)) {
          if (!isSignUpLoaded) {
            handleSignUpNotLoaded();
            return;
          }

          await handleSignUpRedirect(strategy);
        } else {
          handleClerkError(err, setError);
          setLoading(false);
        }
      }
    },
    [
      isSignInLoaded,
      isSignUpLoaded,
      signIn,
      setError,
      setLoading,
      handleSignUpNotLoaded,
      handleSignUpRedirect,
      handleClerkError
    ]
  );

  return {
    handleSocialLogin,
    isOAuthReady,
    isCreatingAccount,
    oauthLoading
  };
};
